import React, { useState, useEffect } from 'react';
import { Inbox, Clock, CheckCircle2, XCircle, AlertCircle, MessageSquareWarning, Ban, Send, RefreshCw, Search } from 'lucide-react';
import api from '../services/api';

const STATUS_LABELS = {
    aguardando_confirmacao: { label: 'Aguardando confirmação do dono', color: '#eab308', icon: Clock },
    aguardando_resposta: { label: 'Enviado — monitorando', color: '#3b82f6', icon: Send },
    respondido_confirmado: { label: 'Confirmado pelo contato', color: '#10b981', icon: CheckCircle2 },
    respondido_recusado: { label: 'Recusado pelo contato', color: '#ef4444', icon: XCircle },
    aguardando_instrucao: { label: 'Aguardando tua resposta', color: '#f97316', icon: MessageSquareWarning },
    cancelado: { label: 'Cancelado', color: '#6b7280', icon: Ban },
    encerrado_pelo_dono: { label: 'Encerrado', color: '#6b7280', icon: Ban },
    erro: { label: 'Erro no envio', color: '#dc2626', icon: AlertCircle },
};

function StatCard({ label, value, color, icon: Icon }) {
    return (
        <div style={{ background:'var(--bg-secondary)', border:'1px solid var(--border)', borderRadius:12, padding:'18px 20px', display:'flex', alignItems:'center', gap:14, minWidth:160 }}>
            <div style={{ width:42, height:42, borderRadius:10, background:color+'22', color:color, display:'flex', alignItems:'center', justifyContent:'center' }}><Icon size={22}/></div>
            <div>
                <div style={{ fontSize:22, fontWeight:600, lineHeight:1 }}>{value}</div>
                <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:4 }}>{label}</div>
            </div>
        </div>
    );
}

export default function OrdensSecretaria() {
    const [ordens, setOrdens] = useState([]);
    const [funcionarios, setFuncionarios] = useState([]);
    const [selectedFunc, setSelectedFunc] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [resumo, setResumo] = useState(null);
    const [busca, setBusca] = useState('');
    const [loading, setLoading] = useState(false);
    const [detalhe, setDetalhe] = useState(null);

    useEffect(() => {
        loadFuncionarios();
    }, []);

    useEffect(() => {
        if (selectedFunc) {
            loadOrdens();
            loadResumo();
        } else {
            setOrdens([]);
            setResumo(null);
        }
    }, [selectedFunc, statusFilter]);

    const loadFuncionarios = async () => {
        try {
            const r = await api.get('/api/funcionarios');
            const comSecretaria = r.data.filter(f => f.secretaria_ativa_enabled);
            setFuncionarios(comSecretaria);
            if (comSecretaria.length === 1) setSelectedFunc(comSecretaria[0].id);
        } catch (e) { console.error(e); }
    };

    const loadOrdens = async () => {
        if (!selectedFunc) return;
        setLoading(true);
        try {
            const params = new URLSearchParams({ funcionario_id: selectedFunc });
            if (statusFilter) params.append('status', statusFilter);
            const r = await api.get('/api/ordens-secretaria?' + params.toString());
            setOrdens(r.data);
        } catch (e) { console.error(e); }
        setLoading(false);
    };

    const loadResumo = async () => {
        if (!selectedFunc) return;
        try {
            const r = await api.get('/api/ordens-secretaria/resumo/' + selectedFunc);
            setResumo(r.data);
        } catch (e) { console.error(e); }
    };

    const encerrar = async (id) => {
        if (!window.confirm('Encerrar o monitoramento desta ordem?')) return;
        try {
            await api.post('/api/ordens-secretaria/' + id + '/encerrar');
            loadOrdens(); loadResumo();
        } catch (e) { alert('Erro ao encerrar'); }
    };

    const estender = async (id) => {
        const h = prompt('Estender monitoramento por quantas horas? (1-168)', '48');
        if (!h) return;
        try {
            await api.post('/api/ordens-secretaria/' + id + '/estender', { horas: parseInt(h) });
            loadOrdens();
        } catch (e) { alert('Erro ao estender'); }
    };

    const ordensFiltradas = ordens.filter(o => {
        if (!busca) return true;
        const b = busca.toLowerCase();
        return (o.destinatario_nome || '').toLowerCase().includes(b) ||
               (o.destinatario_telefone || '').includes(b) ||
               (o.conteudo_bruto || '').toLowerCase().includes(b) ||
               (o.conteudo_humanizado || '').toLowerCase().includes(b);
    });

    const fmtData = (s) => s ? new Date(s).toLocaleString('pt-BR', { day:'2-digit', month:'2-digit', year:'2-digit', hour:'2-digit', minute:'2-digit' }) : '-';

    return (
        <div>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24 }}>
                <div>
                    <h1 style={{ margin:0, fontSize:28, fontWeight:600, display:'flex', alignItems:'center', gap:12 }}>
                        <Inbox size={28} style={{ color:'var(--purple-400)' }}/>
                        Ordens da Secretária
                    </h1>
                    <p style={{ margin:'4px 0 0', color:'var(--text-muted)', fontSize:14 }}>Histórico de envios e monitoramento de respostas</p>
                </div>
                <button onClick={() => { loadOrdens(); loadResumo(); }} style={{ display:'flex', alignItems:'center', gap:8, background:'var(--bg-secondary)', border:'1px solid var(--border)', color:'var(--text-primary)', padding:'10px 16px', borderRadius:8, cursor:'pointer', fontSize:13 }}>
                    <RefreshCw size={16}/> Atualizar
                </button>
            </div>

            {funcionarios.length === 0 ? (
                <div style={{ background:'var(--bg-secondary)', border:'1px solid var(--border)', borderRadius:12, padding:40, textAlign:'center' }}>
                    <Inbox size={48} style={{ color:'var(--text-muted)', marginBottom:16 }}/>
                    <h3 style={{ margin:0, fontSize:16 }}>Nenhum funcionário com Secretária Ativa</h3>
                    <p style={{ color:'var(--text-muted)', fontSize:14, marginTop:8 }}>Ative o módulo Secretária Ativa em um funcionário para começar a usar.</p>
                </div>
            ) : (
                <>
                    <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap' }}>
                        <select value={selectedFunc} onChange={e => setSelectedFunc(e.target.value)} style={{ padding:'10px 14px', borderRadius:8, background:'var(--bg-secondary)', border:'1px solid var(--border)', color:'var(--text-primary)', fontSize:14, minWidth:240 }}>
                            <option value="">Selecione um funcionário</option>
                            {funcionarios.map(f => <option key={f.id} value={f.id}>{f.nome} — {f.cliente_nome}</option>)}
                        </select>
                        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ padding:'10px 14px', borderRadius:8, background:'var(--bg-secondary)', border:'1px solid var(--border)', color:'var(--text-primary)', fontSize:14 }}>
                            <option value="">Todos os status</option>
                            {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                        </select>
                        <div style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 14px', borderRadius:8, background:'var(--bg-secondary)', border:'1px solid var(--border)', flex:1, minWidth:200 }}>
                            <Search size={16} style={{ color:'var(--text-muted)' }}/>
                            <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar por nome, telefone ou conteúdo..." style={{ flex:1, background:'transparent', border:'none', color:'var(--text-primary)', fontSize:14, outline:'none' }}/>
                        </div>
                    </div>

                    {resumo && (
                        <div style={{ display:'flex', gap:12, marginBottom:24, flexWrap:'wrap' }}>
                            <StatCard label="Total" value={resumo.total} color="#7f77dd" icon={Inbox}/>
                            <StatCard label="Hoje" value={resumo.hoje} color="#7f77dd" icon={Clock}/>
                            <StatCard label="Monitorando" value={resumo.aguardando_resposta} color="#3b82f6" icon={Send}/>
                            <StatCard label="Confirmados" value={resumo.confirmados} color="#10b981" icon={CheckCircle2}/>
                            <StatCard label="Recusados" value={resumo.recusados} color="#ef4444" icon={XCircle}/>
                            <StatCard label="Precisa tua resposta" value={resumo.aguardando_instrucao} color="#f97316" icon={MessageSquareWarning}/>
                            <StatCard label="Com erro" value={resumo.com_erro} color="#dc2626" icon={AlertCircle}/>
                        </div>
                    )}

                    <div style={{ background:'var(--bg-secondary)', border:'1px solid var(--border)', borderRadius:12, overflow:'hidden' }}>
                        {loading ? (
                            <div style={{ padding:40, textAlign:'center', color:'var(--text-muted)' }}>Carregando...</div>
                        ) : ordensFiltradas.length === 0 ? (
                            <div style={{ padding:40, textAlign:'center', color:'var(--text-muted)' }}>Nenhuma ordem encontrada</div>
                        ) : (
                            <table style={{ width:'100%', borderCollapse:'collapse' }}>
                                <thead>
                                    <tr style={{ background:'var(--bg-primary)', borderBottom:'1px solid var(--border)' }}>
                                        <th style={{ padding:'12px 16px', textAlign:'left', fontSize:12, fontWeight:500, color:'var(--text-muted)', textTransform:'uppercase' }}>Status</th>
                                        <th style={{ padding:'12px 16px', textAlign:'left', fontSize:12, fontWeight:500, color:'var(--text-muted)', textTransform:'uppercase' }}>Destinatário</th>
                                        <th style={{ padding:'12px 16px', textAlign:'left', fontSize:12, fontWeight:500, color:'var(--text-muted)', textTransform:'uppercase' }}>Mensagem</th>
                                        <th style={{ padding:'12px 16px', textAlign:'left', fontSize:12, fontWeight:500, color:'var(--text-muted)', textTransform:'uppercase' }}>Enviada</th>
                                        <th style={{ padding:'12px 16px', textAlign:'left', fontSize:12, fontWeight:500, color:'var(--text-muted)', textTransform:'uppercase' }}>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ordensFiltradas.map(o => {
                                        const st = STATUS_LABELS[o.status] || { label:o.status, color:'#6b7280', icon:Clock };
                                        const StIcon = st.icon;
                                        return (
                                            <tr key={o.id} onClick={() => setDetalhe(o)} style={{ borderBottom:'1px solid var(--border)', cursor:'pointer', transition:'background 0.15s' }} onMouseEnter={e => e.currentTarget.style.background='rgba(127,119,221,0.04)'} onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                                                <td style={{ padding:'14px 16px' }}>
                                                    <div style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'4px 10px', borderRadius:16, background:st.color+'22', color:st.color, fontSize:12, fontWeight:500 }}>
                                                        <StIcon size={13}/>{st.label}
                                                    </div>
                                                </td>
                                                <td style={{ padding:'14px 16px', fontSize:14 }}>
                                                    <div style={{ fontWeight:500 }}>{o.destinatario_nome || '(sem nome)'}</div>
                                                    <div style={{ color:'var(--text-muted)', fontSize:12, marginTop:2 }}>{o.destinatario_telefone}</div>
                                                </td>
                                                <td style={{ padding:'14px 16px', fontSize:13, maxWidth:360 }}>
                                                    <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{o.conteudo_humanizado || o.conteudo_bruto}</div>
                                                    {o.total_respostas > 0 && <div style={{ color:'var(--purple-400)', fontSize:11, marginTop:4 }}>💬 {o.total_respostas} resposta(s)</div>}
                                                </td>
                                                <td style={{ padding:'14px 16px', fontSize:12, color:'var(--text-muted)' }}>{fmtData(o.executado_em || o.criado_em)}</td>
                                                <td style={{ padding:'14px 16px' }} onClick={e => e.stopPropagation()}>
                                                    {(o.status === 'aguardando_resposta' || o.status === 'aguardando_instrucao') && (
                                                        <div style={{ display:'flex', gap:6 }}>
                                                            <button onClick={() => estender(o.id)} style={{ padding:'6px 10px', fontSize:11, background:'transparent', border:'1px solid var(--border)', color:'var(--text-secondary)', borderRadius:6, cursor:'pointer' }}>Estender</button>
                                                            <button onClick={() => encerrar(o.id)} style={{ padding:'6px 10px', fontSize:11, background:'transparent', border:'1px solid #ef4444', color:'#ef4444', borderRadius:6, cursor:'pointer' }}>Encerrar</button>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                </>
            )}

            {detalhe && (
                <div onClick={() => setDetalhe(null)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:200, padding:20 }}>
                    <div onClick={e => e.stopPropagation()} style={{ background:'var(--bg-secondary)', border:'1px solid var(--border)', borderRadius:12, padding:28, maxWidth:640, width:'100%', maxHeight:'85vh', overflowY:'auto' }}>
                        <h2 style={{ margin:'0 0 20px', fontSize:20 }}>Detalhes da ordem</h2>
                        <div style={{ display:'grid', gap:14, fontSize:14 }}>
                            <div><strong style={{ color:'var(--text-muted)' }}>Destinatário:</strong><br/>{detalhe.destinatario_nome || '(sem nome)'} — {detalhe.destinatario_telefone}</div>
                            <div><strong style={{ color:'var(--text-muted)' }}>Recado original (como o dono disse):</strong><br/><div style={{ padding:12, background:'var(--bg-primary)', borderRadius:8, marginTop:6 }}>{detalhe.conteudo_bruto}</div></div>
                            <div><strong style={{ color:'var(--text-muted)' }}>Mensagem enviada (humanizada):</strong><br/><div style={{ padding:12, background:'var(--bg-primary)', borderRadius:8, marginTop:6 }}>{detalhe.conteudo_humanizado || '(não humanizado)'}</div></div>
                            {detalhe.ultima_resposta_destinatario && (
                                <div><strong style={{ color:'var(--text-muted)' }}>Última resposta do destinatário:</strong><br/><div style={{ padding:12, background:'var(--bg-primary)', borderRadius:8, marginTop:6 }}>{detalhe.ultima_resposta_destinatario}</div><div style={{ fontSize:11, color:'var(--text-muted)', marginTop:4 }}>{fmtData(detalhe.ultima_resposta_em)} • {detalhe.total_respostas} resposta(s) total</div></div>
                            )}
                            <div style={{ display:'flex', gap:20, fontSize:12, color:'var(--text-muted)' }}>
                                <div><strong>Criada:</strong> {fmtData(detalhe.criado_em)}</div>
                                {detalhe.executado_em && <div><strong>Enviada:</strong> {fmtData(detalhe.executado_em)}</div>}
                                {detalhe.monitorar_ate && <div><strong>Monitora até:</strong> {fmtData(detalhe.monitorar_ate)}</div>}
                            </div>
                            {detalhe.resultado && <div style={{ fontSize:12, color:'var(--text-muted)' }}><strong>Resultado:</strong> {detalhe.resultado}</div>}
                        </div>
                        <div style={{ marginTop:24, display:'flex', justifyContent:'flex-end', gap:10 }}>
                            <button onClick={() => setDetalhe(null)} style={{ padding:'10px 20px', background:'var(--bg-primary)', border:'1px solid var(--border)', color:'var(--text-primary)', borderRadius:8, cursor:'pointer', fontSize:13 }}>Fechar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
